/**
 * Tutorial / course steps inside the create agent: plan curriculum → generate
 * lesson artifacts → run alignment pipeline → preview. Status lives in the right panel.
 */
import React, { useEffect, useState } from 'react';
import {
  BookOpen,
  Check,
  CheckCircle2,
  Circle,
  Compass,
  ExternalLink,
  Loader2,
  Play,
  Wand2,
  X,
  XCircle,
} from 'lucide-react';
import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { scriptAudioAlignStepLabel } from '@/lib/scriptAudioAlignPipeline';
import { postScriptAudioAlignProcess } from '@/lib/scriptAudioAlign/processRunner';
import {
  COURSE_DEPTHS,
  COURSE_NARRATION_LANGUAGES,
  COURSE_NARRATION_TONES,
  applyCourseExplainVisualPlan,
  courseLanguageMeta,
  courseStepLabel,
  getCourseExplain,
  joinExplainNarrationScript,
  planCourse,
  startCourseExplain,
} from '@/lib/scriptAudioAlignCourse';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const COURSE_STAGES = ['plan', 'artifacts', 'align', 'done'];

export function usesCourse(plan) {
  return plan?.flow?.id === 'script_audio_align';
}

export function courseStage(course) {
  if (!course) return 'plan';
  if (course.align_status === 'completed' || course.stage === 'aligned' || course.stage === 'done') {
    return 'done';
  }
  if (
    course.align_status === 'running'
    || course.align_status === 'failed'
    || course.align_result
    || course.explain_status === 'completed'
  ) {
    return 'align';
  }
  if (course.explain_job_id || course.plan) return 'artifacts';
  return 'plan';
}

export function lengthToCourseDepth(length) {
  if (length === 'short') return 'quick';
  if (length === 'long') return 'deep';
  return 'standard';
}

/** Map create-agent voice settings onto script-audio-align TTS. */
export function ttsOptionsFromPlan(creationPlan, course) {
  const lang = course?.language || creationPlan?.input?.language || 'en';
  const meta = courseLanguageMeta(lang);
  const provider = String(creationPlan?.input?.voice?.provider || '').toLowerCase();
  const voiceId = String(creationPlan?.input?.voice?.voice_id || '').trim();
  if (provider === 'murf') {
    return {
      ttsProvider: 'murf',
      voice: voiceId || meta.murfVoice,
      language: lang,
    };
  }
  return {
    ttsProvider: 'edge',
    voice: meta.edgeVoice,
    language: lang,
  };
}

/** Persist course progress on the plan locally so refresh does not lose the job. */
const COURSE_STORE_KEY = 'twinnee.createAgent.course';

export function loadCourseState(planId) {
  if (!planId || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(`${COURSE_STORE_KEY}.${planId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveCourseState(planId, course) {
  if (!planId || typeof sessionStorage === 'undefined') return;
  try {
    if (!course) sessionStorage.removeItem(`${COURSE_STORE_KEY}.${planId}`);
    else sessionStorage.setItem(`${COURSE_STORE_KEY}.${planId}`, JSON.stringify(course));
  } catch {
    /* ignore */
  }
}

export async function runCoursePlan(plan) {
  const title = String(plan?.input?.title || '').trim();
  if (!title) throw new Error('Add a title before planning the course');
  const notes = [plan?.input?.brief, plan?.input?.script].filter(Boolean).join('\n\n');
  const depth = lengthToCourseDepth(plan?.input?.length);
  const data = await planCourse({
    title,
    notes: notes || undefined,
    audience: plan?.input?.audience || undefined,
    depth,
  });
  return {
    stage: 'artifacts',
    plan: data,
    depth,
    language: plan?.input?.language || 'en',
    tone: 'serious',
    explain_job_id: null,
    explain_status: null,
    explain_job: null,
    at: new Date().toISOString(),
  };
}

export async function runCourseExplain(course, { language, tone, title } = {}) {
  const plan = course?.plan;
  if (!plan?.curriculum?.topics?.length) throw new Error('Plan a curriculum first');
  const lang = language || course.language || 'en';
  const narrationTone = tone || course.tone || 'serious';
  const courseTitle = String(title || plan.title || course.title || '').trim();
  if (!courseTitle) throw new Error('Add a title before generating lesson artifacts');
  const data = await startCourseExplain({
    title: courseTitle,
    difficulty: plan.domain_analysis?.difficulty || 'intermediate',
    curriculum: plan.curriculum,
    blueprint: plan.blueprint,
    artifactSelection: plan.artifact_selection,
    language: lang,
    narrationTone,
  });
  if (!data?.job_id) throw new Error('Artifact queue did not return a job id');
  return {
    ...course,
    stage: 'artifacts',
    language: lang,
    tone: narrationTone,
    explain_job_id: data.job_id,
    explain_status: data.status || 'queued',
    explain_job: null,
    at: new Date().toISOString(),
  };
}

export async function pollCourseExplain(jobId) {
  return getCourseExplain(jobId);
}

export function courseNarrationScript(course, title = '') {
  const job = course?.explain_job;
  if (!job) return '';
  return (
    joinExplainNarrationScript(job, title).trim()
    || String(job.generated_script || '').trim()
  );
}

/**
 * Create/update a story, run script→audio alignment, then apply course-explain visuals.
 * Calls onProgress with partial align results for the pipeline popup / right panel.
 */
export async function runCourseAlignment(course, creationPlan, { onProgress } = {}) {
  const explainJob = course?.explain_job;
  const explainJobId = course?.explain_job_id || explainJob?.job_id;
  if (!explainJobId) throw new Error('Generate lesson artifacts first');
  const title = String(creationPlan?.input?.title || course?.plan?.title || '').trim();
  if (!title) throw new Error('Add a title before running alignment');
  const narrationScript = courseNarrationScript(course, title);
  if (!narrationScript || narrationScript.trim().length < 20) {
    throw new Error('No narration script yet — wait for artifact generation to finish');
  }
  if (!explainJob?.units?.length) {
    throw new Error('No lesson artifacts to align yet');
  }

  const { ttsProvider, voice, language } = ttsOptionsFromPlan(creationPlan, course);

  let linkedStoryId = course.align_story_id || null;
  if (!linkedStoryId) {
    const { data: story } = await api.post('/stories', {
      title,
      user_full_story: narrationScript,
      user_topic: creationPlan?.input?.brief || title,
      video_style: 'narrated',
      story_type: 'original',
      tone: creationPlan?.input?.tone || 'informative',
    });
    linkedStoryId = story.id;
    onProgress?.({ story_id: linkedStoryId, status: 'running', pipeline_steps: [] });
  } else {
    await api.patch(`/stories/${linkedStoryId}`, {
      title,
      user_full_story: narrationScript,
      user_topic: creationPlan?.input?.brief || title,
    });
  }

  const aligned = await postScriptAudioAlignProcess(
    {
      script: narrationScript,
      title,
      story_id: linkedStoryId,
      tts_provider: ttsProvider,
      voice: voice || undefined,
      language: language || undefined,
    },
    {
      onProgress: (partial) => {
        onProgress?.({ ...partial, story_id: linkedStoryId });
      },
    }
  );

  if (aligned?.status !== 'completed') {
    throw new Error(aligned?.error_message || 'Alignment did not complete');
  }

  onProgress?.({ ...aligned, story_id: linkedStoryId, status: 'running' });

  const preview = await applyCourseExplainVisualPlan(aligned.job_id, explainJobId, {
    buildPreview: true,
  });

  const finalStoryId = preview.story_id || aligned.story_id || linkedStoryId;
  const finalJobId = preview.job_id || aligned.job_id;
  const result = { ...preview, story_id: finalStoryId, job_id: finalJobId, status: 'completed' };
  onProgress?.(result);

  return {
    ...course,
    stage: 'aligned',
    align_status: 'completed',
    align_story_id: finalStoryId,
    align_job_id: finalJobId,
    align_result: result,
    align_error: null,
    narration_script: narrationScript,
    at: new Date().toISOString(),
  };
}

function StageIcon({ status }) {
  if (status === 'done') return <CheckCircle2 className="w-3.5 h-3.5 text-[#2DD4BF]" />;
  if (status === 'failed') return <XCircle className="w-3.5 h-3.5 text-red-400" />;
  if (status === 'active') return <Loader2 className="w-3.5 h-3.5 text-amber-300 animate-spin" />;
  return <Circle className="w-3.5 h-3.5 text-[#6B7280]" />;
}

function stepState(step, stage, course, busyStep) {
  const idx = COURSE_STAGES.indexOf(step);
  const cur = COURSE_STAGES.indexOf(stage);
  if (step === 'artifacts' && course?.explain_status === 'failed') return 'failed';
  if (step === 'align' && course?.align_status === 'failed') return 'failed';
  if (busyStep === 'plan' && step === 'plan') return 'active';
  if (busyStep === 'explain' && step === 'artifacts') return 'active';
  if (busyStep === 'align' && step === 'align') return 'active';
  if (step === 'artifacts' && (course?.explain_status === 'running' || course?.explain_status === 'queued')) {
    return 'active';
  }
  if (step === 'align' && course?.align_status === 'running') return 'active';
  if (idx < cur) return 'done';
  if (idx === cur) return 'active';
  return 'todo';
}

/** Right-panel course status: plan → artifacts → alignment → done. */
export function CourseStatusPanel({
  course,
  busyStep,
  onOpenNarration,
  onOpenAlignPopup,
  onOpenEditor,
}) {
  if (!course && !busyStep) return null;
  const stage = courseStage(course);
  const explain = course?.explain_job;
  const plan = course?.plan;
  const align = course?.align_result;
  const alignSteps = Array.isArray(align?.pipeline_steps) ? align.pipeline_steps : [];
  const pipeline = Array.isArray(plan?.pipeline_steps) ? plan.pipeline_steps : [];
  const topics = plan?.curriculum?.topics || [];
  const total = explain?.total_units || 0;
  const done = explain?.completed_units || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const alignDone = alignSteps.filter((s) => s.status === 'completed').length;
  const previewUrl = align?.preview_video_url || align?.video_url || align?.audio_url || '';

  const labels = {
    plan: busyStep === 'plan' ? 'Planning course…' : plan ? 'Course planned' : 'Plan course',
    artifacts: busyStep === 'explain'
      ? 'Starting artifact queue…'
      : course?.explain_job_id
        ? `Artifacts ${done}/${total || '…'}`
        : 'Generate lesson artifacts',
    align: busyStep === 'align' || course?.align_status === 'running'
      ? 'Running alignment…'
      : course?.align_status === 'completed'
        ? 'Alignment complete'
        : course?.align_status === 'failed'
          ? 'Alignment failed'
          : 'Run alignment pipeline',
    done: course?.align_status === 'completed' ? 'Preview ready' : 'Ready for alignment',
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#11151e]/95 p-4 space-y-4" data-testid="course-status-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 text-[#2DD4BF]" />
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280] font-semibold">Course pipeline</p>
        </div>
        <span className="text-[10px] font-mono text-[#9CA3AF]">
          {stage === 'done' ? 'ready' : COURSE_STAGES[COURSE_STAGES.indexOf(stage)] || 'plan'}
        </span>
      </div>

      <ol className="space-y-2">
        {COURSE_STAGES.map((step) => {
          const state = stepState(step, stage, course, busyStep);
          return (
            <li key={step} className="flex items-start gap-2 text-xs text-[#E5E7EB]">
              <StageIcon status={state} />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{labels[step]}</p>
                {step === 'plan' && topics.length ? (
                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                    {topics.length} topic{topics.length === 1 ? '' : 's'} · {plan?.used_llm ? 'AI-planned' : 'Heuristic'}
                  </p>
                ) : null}
                {step === 'artifacts' && course?.explain_job_id ? (
                  <div className="mt-1.5 space-y-1">
                    <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#2DD4BF] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-[#9CA3AF]">
                      {(course.language || 'en').toUpperCase()} · {course.tone === 'genz' ? 'Funny GenZ' : 'Serious'}
                      {course.explain_status === 'failed' ? ' · failed' : ''}
                    </p>
                  </div>
                ) : null}
                {step === 'align' && alignSteps.length ? (
                  <div className="mt-1.5 space-y-1">
                    <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-[#A78BFA] transition-all"
                        style={{ width: `${Math.round((alignDone / alignSteps.length) * 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-[#9CA3AF]">
                      {alignDone}/{alignSteps.length} pipeline steps
                      {onOpenAlignPopup ? (
                        <>
                          {' · '}
                          <button
                            type="button"
                            onClick={onOpenAlignPopup}
                            className="text-[#C4B5FD] hover:underline cursor-pointer"
                          >
                            Open popup
                          </button>
                        </>
                      ) : null}
                    </p>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {pipeline.length ? (
        <div className="space-y-1.5 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Plan stages</p>
          {pipeline.map((s) => (
            <div key={s.step_id} className="flex items-center gap-2 text-[11px] text-[#cbd5e1]">
              {s.status === 'completed' ? (
                <Check className="w-3 h-3 text-[#2DD4BF]" />
              ) : s.status === 'running' ? (
                <Loader2 className="w-3 h-3 animate-spin text-amber-300" />
              ) : (
                <Compass className="w-3 h-3 text-[#6B7280]" />
              )}
              {courseStepLabel(s)}
            </div>
          ))}
        </div>
      ) : null}

      {topics.length ? (
        <div className="space-y-1.5 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Curriculum</p>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {topics.map((t, i) => (
              <li key={`${t.title}-${i}`} className="text-[11px] text-[#E5E7EB]">
                <span className="text-[#2DD4BF] font-mono mr-1">{i + 1}.</span>
                {t.title || 'Topic'}
                {Array.isArray(t.subtopics) && t.subtopics.length ? (
                  <span className="text-[#9CA3AF]"> · {t.subtopics.length} subtopics</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {explain?.units?.length ? (
        <div className="space-y-1.5 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Artifact units</p>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {explain.units.map((u, i) => (
              <li key={`${u.topic_title}-${u.subtopic}-${i}`} className="flex items-center gap-2 text-[11px] text-[#E5E7EB]">
                <StageIcon
                  status={
                    u.status === 'completed' ? 'done' : u.status === 'failed' ? 'failed' : u.status === 'running' ? 'active' : 'todo'
                  }
                />
                <span className="truncate">
                  {u.unit_kind === 'topic' ? u.topic_title : u.subtopic || u.topic_title || `Unit ${i + 1}`}
                </span>
                {u.artifacts?.length ? (
                  <span className="text-[#6B7280] shrink-0">{u.artifacts.length} art.</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {alignSteps.length ? (
        <div className="space-y-1.5 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Alignment steps</p>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {alignSteps.slice(0, 12).map((s, i) => (
              <li key={`${s.step_id || s.title}-${i}`} className="flex items-center gap-2 text-[11px] text-[#E5E7EB]">
                <StageIcon
                  status={
                    s.status === 'completed' ? 'done' : s.status === 'failed' ? 'failed' : s.status === 'running' ? 'active' : 'todo'
                  }
                />
                <span className="truncate">{scriptAudioAlignStepLabel(s)}</span>
              </li>
            ))}
            {alignSteps.length > 12 ? (
              <li className="text-[10px] text-[#6B7280]">+{alignSteps.length - 12} more…</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {course?.align_status === 'completed' && (previewUrl || course.align_job_id) ? (
        <div className="space-y-2 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Output</p>
          {align?.audio_url ? (
            <audio
              controls
              className="w-full h-8"
              preload="metadata"
              src={mediaUrlForPlayback(align.audio_url)}
            />
          ) : null}
          {(align?.preview_video_url || align?.video_url) ? (
            <video
              controls
              className="w-full rounded-lg border border-white/10 max-h-40 bg-black"
              src={mediaUrlForPlayback(align.preview_video_url || align.video_url)}
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            {onOpenAlignPopup ? (
              <button
                type="button"
                onClick={onOpenAlignPopup}
                className="px-2.5 py-1.5 rounded-full text-[11px] border border-white/15 text-[#E5E7EB] hover:border-[#A78BFA]/50 cursor-pointer"
              >
                Pipeline details
              </button>
            ) : null}
            {onOpenEditor && course.align_job_id ? (
              <button
                type="button"
                onClick={onOpenEditor}
                className="px-2.5 py-1.5 rounded-full text-[11px] border border-[#2DD4BF]/40 text-[#5EEAD4] hover:bg-[#2DD4BF]/10 cursor-pointer inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> Open in editor
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {course?.align_error ? (
        <p className="text-[11px] text-red-300 border-t border-white/5 pt-3">{course.align_error}</p>
      ) : null}

      {stage === 'artifacts' && !course?.explain_job_id && onOpenNarration ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenNarration();
          }}
          disabled={busyStep === 'explain' || busyStep === 'plan'}
          className="w-full py-2 rounded-xl text-xs font-semibold border border-[#2DD4BF]/40 text-[#5EEAD4] hover:bg-[#2DD4BF]/10 cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="course-status-generate-artifacts"
        >
          {busyStep === 'explain' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Wand2 className="w-3.5 h-3.5" />
          )}
          {busyStep === 'explain' ? 'Starting…' : 'Generate lesson artifacts…'}
        </button>
      ) : null}

      {stage === 'align' && course?.explain_status === 'completed' && course?.align_status !== 'running' && onOpenAlignPopup ? (
        <button
          type="button"
          onClick={onOpenAlignPopup}
          disabled={busyStep === 'align'}
          className="w-full py-2 rounded-xl text-xs font-semibold border border-[#A78BFA]/40 text-[#C4B5FD] hover:bg-[#A78BFA]/10 cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="course-status-align"
        >
          {busyStep === 'align' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {busyStep === 'align' ? 'Aligning…' : course?.align_status === 'failed' ? 'Retry alignment' : 'View alignment'}
        </button>
      ) : null}
    </div>
  );
}

/** Popup: narration language + tone before starting the artifact queue. */
export function CourseNarrationDialog({ open, language, tone, depths, depth, onClose, onConfirm, busy }) {
  const [lang, setLang] = useState(language || 'en');
  const [style, setStyle] = useState(tone || 'serious');
  const [courseDepth, setCourseDepth] = useState(depth || 'standard');

  useEffect(() => {
    if (!open) return;
    setLang(language || 'en');
    setStyle(tone || 'serious');
    setCourseDepth(depth || 'standard');
  }, [open, language, tone, depth]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
      data-testid="course-narration-dialog"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Lesson artifact options"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-white/15 bg-[#0c1018] shadow-2xl p-5 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <TwinneeLogo className="w-4 h-4 text-[#2DD4BF]" /> Generate lesson artifacts
            </p>
            <p className="text-[11px] text-[#9CA3AF] mt-1">
              Each topic and subtopic gets visuals plus spoken narration in the language you pick.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white flex items-center justify-center cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block space-y-1.5">
          <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Narration language</span>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white"
          >
            {COURSE_NARRATION_LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </label>

        <label className="block space-y-1.5">
          <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Narration style</span>
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white"
          >
            {COURSE_NARRATION_TONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}{t.hint ? ` — ${t.hint}` : ''}
              </option>
            ))}
          </select>
        </label>

        {depths ? (
          <label className="block space-y-1.5">
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Course depth</span>
            <select
              value={courseDepth}
              onChange={(e) => setCourseDepth(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white"
            >
              {COURSE_DEPTHS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>
        ) : null}

        <button
          type="button"
          disabled={busy}
          onClick={() => onConfirm?.({ language: lang, tone: style, depth: courseDepth })}
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60"
          data-testid="course-narration-confirm"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {busy ? 'Starting…' : 'Start artifact generation'}
        </button>
      </div>
    </div>
  );
}

/** In-studio alignment pipeline popup (must sit above create-agent z-200). */
export function CourseAlignPipelinePopup({ open, onClose, result, loading, error }) {
  if (!open) return null;
  const steps = Array.isArray(result?.pipeline_steps) ? result.pipeline_steps : [];
  const done = steps.filter((s) => s.status === 'completed').length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : loading ? 8 : 0;
  const isComplete = result?.status === 'completed';
  const isFailed = result?.status === 'failed' || Boolean(error);
  const isRunning = loading || result?.status === 'running';

  return (
    <div
      className="fixed inset-0 z-[230] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
      data-testid="course-align-pipeline-popup"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Alignment pipeline"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-white/15 bg-[#0c1018] shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              {isRunning && !isComplete ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#A78BFA]" />
              ) : isComplete ? (
                <CheckCircle2 className="w-4 h-4 text-[#2DD4BF]" />
              ) : isFailed ? (
                <XCircle className="w-4 h-4 text-red-400" />
              ) : (
                <Play className="w-4 h-4 text-[#9CA3AF]" />
              )}
              Script → audio alignment
            </p>
            <p className="text-[11px] text-[#9CA3AF] mt-1">
              {isRunning && !isComplete
                ? 'Running pipeline…'
                : isComplete
                  ? 'Pipeline complete'
                  : isFailed
                    ? 'Pipeline failed'
                    : 'Ready'}
              {steps.length ? ` · ${done}/${steps.length} steps` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white flex items-center justify-center cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#A78BFA] transition-all" style={{ width: `${pct}%` }} />
          </div>

          {(error || result?.error_message) ? (
            <div className="rounded-xl border border-red-500/30 bg-red-950/30 p-3 text-xs text-red-300">
              {error || result.error_message}
            </div>
          ) : null}

          {result?.audio_url ? (
            <div className="rounded-xl border border-[#A78BFA]/25 bg-[#A78BFA]/[0.06] p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-[#C4B5FD] font-semibold">Master audio</p>
              <audio controls className="w-full h-9" preload="metadata" src={mediaUrlForPlayback(result.audio_url)} />
              <p className="text-[11px] text-[#9CA3AF]">
                Duration: {Number(result.duration_seconds || 0).toFixed(2)}s
                {result.alignment_source ? ` · ${result.alignment_source}` : ''}
              </p>
            </div>
          ) : null}

          {(result?.preview_video_url || result?.video_url) ? (
            <video
              controls
              className="w-full rounded-xl border border-white/10 max-h-48 bg-black"
              src={mediaUrlForPlayback(result.preview_video_url || result.video_url)}
            />
          ) : null}

          {steps.length ? (
            <ul className="space-y-1.5">
              {steps.map((s, i) => (
                <li
                  key={`${s.step_id || s.title}-${i}`}
                  className="flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-[#E5E7EB]"
                >
                  <StageIcon
                    status={
                      s.status === 'completed' ? 'done' : s.status === 'failed' ? 'failed' : s.status === 'running' ? 'active' : 'todo'
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{scriptAudioAlignStepLabel(s)}</p>
                    {s.error ? <p className="text-[10px] text-red-300 mt-0.5">{s.error}</p> : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[#9CA3AF]">
              {loading ? 'Starting alignment…' : 'Pipeline steps will appear here as they run.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
